import React, { useState, useCallback, useMemo, useEffect, useRef, MutableRefObject } from 'react';
import {
    Box,
    Paper,
    Text,
    Title,
    Textarea,
    Button,
    Alert,
    Loader,
    Tabs,
    Tooltip,
    ActionIcon,
    Modal,
    Table,
    Group,
    Pagination,
    Select,
} from '@mantine/core';
import {
    IconAlertTriangle,
    IconCircleCheck,
    IconEye,
    IconCopy,
    IconRefresh,
    IconX,
} from '@tabler/icons-react';
import { LoadingState } from '../../common/LoadingState';
import { JsonDiffViewer } from '../../common/JsonDiffViewer';
import { JsonViewer } from '../../common/JsonViewer';
import { FilterPreviewDialog } from '../../dialogs';
import { ScimEntityService } from '../../../services/ScimEntityService';
import { useLegacyConversion } from '../hooks/useLegacyConversion';
import { copyToClipboard } from '../../../utils/clipboardUtils';

interface LegacyConverterTabProps {
    // Service reference
    entityServiceRef: MutableRefObject<ScimEntityService | undefined>;

    // Shared state (used by both Legacy Converter and Legacy Bulk tabs)
    legacySuccess: string | null;
    legacyError: string | null;
    setLegacySuccess: (value: string | null) => void;
    setLegacyError: (value: string | null) => void;
}

export const LegacyConverterTab: React.FC<LegacyConverterTabProps> = ({
    entityServiceRef,
    legacySuccess,
    legacyError,
    setLegacySuccess,
    setLegacyError
}) => {
    // ========== Internal State ==========

    // Legacy filter input and parsed data
    const [legacyFilter, setLegacyFilter] = useState<string>('');
    const [legacyParsedData, setLegacyParsedData] = useState<{ entityType: string; filter: string } | null>(null);

    // Selected group state
    const [selectedGroupDisplayName, setSelectedGroupDisplayName] = useState<string | null>(null);
    const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
    const [selectedGroupData, setSelectedGroupData] = useState<any | null>(null);
    const [loadingSelectedGroup, setLoadingSelectedGroup] = useState(false);

    // UI state
    const [legacyTabView, setLegacyTabView] = useState(0);
    const [legacyLoading, setLegacyLoading] = useState(false);
    const [filterPreviewOpen, setFilterPreviewOpen] = useState(false);

    // Group selection dialog state
    const [groupSelectDialogOpen, setGroupSelectDialogOpen] = useState(false);
    const [groupsWithLegacyFilter, setGroupsWithLegacyFilter] = useState<Array<any>>([]);
    const [loadingGroups, setLoadingGroups] = useState(false);
    const [groupSelectError, setGroupSelectError] = useState<string | null>(null);
    const [groupsTotalResults, setGroupsTotalResults] = useState<number | null>(null);
    const [groupsPage, setGroupsPage] = useState(0);
    const [groupsRowsPerPage, setGroupsRowsPerPage] = useState(25);

    // ========== Hook Integration ==========

    const {
        untranslatedRcIds,
        legacyParseError,
        setLegacyParseError,
        attributeValidationWarning,
        setAttributeValidationWarning,
        rcCacheLoaded,
        loadingRCCache,
        loadResponsibilityCenterCache,
        processFilterPipelineWithDeps,
        parseLegacyFilterWithErrorHandling,
        validateAttributesForPerson
    } = useLegacyConversion(entityServiceRef);

    // ========== Preload RC Cache ==========

    useEffect(() => {
        // Preload RC cache when tab is mounted
        if (!rcCacheLoaded && !loadingRCCache) {
            loadResponsibilityCenterCache();
        }
    }, [rcCacheLoaded, loadingRCCache, loadResponsibilityCenterCache]);

    // ========== Handler Functions ==========

    const handleLegacyFilterChange = useCallback(async (value: string) => {
        setLegacyFilter(value);
        setLegacyParseError(null);
        setLegacyParsedData(null);
        setAttributeValidationWarning([]);

        if (value.trim()) {
            const parsed = parseLegacyFilterWithErrorHandling(value);
            if (parsed) {
                // Check if filter contains RC patterns that need resolution (Rc or Rcs)
                const needsRcResolution = /Rcs?\s+(eq|\$in)/i.test(parsed.filter);

                // Only load RC cache if needed
                if (needsRcResolution) {
                    await loadResponsibilityCenterCache();
                }

                const resolvedFilter = await processFilterPipelineWithDeps(parsed.filter);

                setLegacyParsedData({
                    entityType: parsed.entityType,
                    filter: resolvedFilter
                });

                if (parsed.entityType?.toLowerCase() === 'person') {
                    await validateAttributesForPerson(resolvedFilter);
                }
            }
        }
    }, [parseLegacyFilterWithErrorHandling, processFilterPipelineWithDeps, validateAttributesForPerson, loadResponsibilityCenterCache, setLegacyParseError, setAttributeValidationWarning]);

    const handleConvertGroupFromLegacy = useCallback(async () => {
        if (!legacyParsedData || !selectedGroupId) {
            setLegacyError('Please select a group with a valid legacy filter');
            return;
        }

        setLegacyLoading(true);
        setLegacyError(null);
        setLegacySuccess(null);

        try {
            const patchOperations = [
                // Clear property (replace with null; "remove" is only for array elements)
                { op: 'replace', path: 'LegacyFilter', value: null },
                { op: 'replace', path: 'FilterScope', value: legacyParsedData.entityType },
                { op: 'replace', path: 'Filter', value: legacyParsedData.filter },
                { op: 'replace', path: 'Managed', value: true }
            ];

            const patchRequest = {
                schemas: ['urn:ietf:params:scim:api:messages:2.0:PatchOp'],
                Operations: patchOperations
            };

            await entityServiceRef.current!.patchEntity(selectedGroupId, patchRequest, 'Group');

            setLegacySuccess(`Successfully converted group "${selectedGroupDisplayName}" to dynamic group`);

            // Reload the selected group data
            const updatedGroup = await entityServiceRef.current!.getGroupById(selectedGroupId);
            setSelectedGroupData(updatedGroup);

        } catch (err) {
            console.error('Error converting group:', err);
            setLegacyError(err instanceof Error ? err.message : 'Failed to convert group');
        } finally {
            setLegacyLoading(false);
        }
    }, [legacyParsedData, selectedGroupId, selectedGroupDisplayName, entityServiceRef, setLegacySuccess, setLegacyError]);

    const loadGroupsWithLegacyFilter = useCallback(async (page: number, pageSize: number) => {
        setLoadingGroups(true);
        setGroupSelectError(null);
        try {
            const response = await entityServiceRef.current!.searchGroups({
                filter: 'LegacyFilter pr',
                page,
                pageSize,
                attributes: ['Id', 'DisplayName', 'LegacyFilter', 'FilterScope', 'Filter', 'Managed']
            });
            setGroupsWithLegacyFilter(response.groups || []);
            setGroupsTotalResults(response.totalResults || 0);
        } catch (err) {
            console.error('Error loading groups with legacy filter:', err);
            setGroupSelectError(err instanceof Error ? err.message : 'Failed to load groups');
        } finally {
            setLoadingGroups(false);
        }
    }, [entityServiceRef]);

    const handleSelectGroupForConversion = useCallback(async (group: any) => {
        setLoadingSelectedGroup(true);
        try {
            const fullGroup = await entityServiceRef.current!.getGroupById(group.Id || group.id);
            setSelectedGroupData(fullGroup);
            setSelectedGroupId(group.Id || group.id);
            setSelectedGroupDisplayName(group.DisplayName || group.displayName);

            // Parse the legacy filter
            const legacyFilterValue = fullGroup.LegacyFilter || fullGroup.legacyFilter;
            if (legacyFilterValue) {
                setLegacyFilter(legacyFilterValue);
                await handleLegacyFilterChange(legacyFilterValue);
            }
        } catch (err) {
            console.error('Error loading group for conversion:', err);
            setLegacyError(err instanceof Error ? err.message : 'Failed to load group');
        } finally {
            setLoadingSelectedGroup(false);
        }
    }, [entityServiceRef, handleLegacyFilterChange, setLegacyError]);

    const handleClearLegacyState = useCallback(() => {
        setLegacyFilter('');
        setLegacyParseError(null);
        setLegacyParsedData(null);
        setSelectedGroupDisplayName(null);
        setSelectedGroupId(null);
        setSelectedGroupData(null);
        setAttributeValidationWarning([]);
        setLegacyTabView(0);
        setLegacySuccess(null);
        setLegacyError(null);
    }, [setLegacyParseError, setAttributeValidationWarning, setLegacySuccess, setLegacyError]);

    const handleOpenGroupSelect = useCallback(async () => {
        setGroupSelectDialogOpen(true);
        await loadGroupsWithLegacyFilter(1, groupsRowsPerPage);
    }, [loadGroupsWithLegacyFilter, groupsRowsPerPage]);

    const handleCopyFilter = useCallback(() => {
        if (legacyParsedData?.filter) {
            copyToClipboard(legacyParsedData.filter);
        }
    }, [legacyParsedData]);

    // Handle page change for Legacy group selection
    const handleGroupsPageChange = useCallback(async (_event: unknown, newPage: number) => {
        setGroupsPage(newPage);
        await loadGroupsWithLegacyFilter(newPage + 1, groupsRowsPerPage);
    }, [loadGroupsWithLegacyFilter, groupsRowsPerPage]);

    // Handle rows per page change for Legacy group selection
    const handleGroupsRowsPerPageChange = useCallback(async (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const newRowsPerPage = parseInt(event.target.value, 10);
        setGroupsRowsPerPage(newRowsPerPage);
        setGroupsPage(0);
        await loadGroupsWithLegacyFilter(1, newRowsPerPage);
    }, [loadGroupsWithLegacyFilter]);

    // ========== Computed Values ==========

    // Compute expected payload for display
    const expectedPayload = useMemo(() => {
        if (!selectedGroupData || !legacyParsedData) {
            return null;
        }

        const patchOperations = [
            {
                op: 'replace',
                path: 'Filter',
                value: legacyParsedData.filter
            },
            {
                op: 'replace',
                path: 'FilterScope',
                value: legacyParsedData.entityType
            },
            {
                op: 'replace',
                path: 'Managed',
                value: true
            }
        ];

        return {
            schemas: ['urn:ietf:params:scim:api:messages:2.0:PatchOp'],
            Operations: patchOperations
        };
    }, [selectedGroupData, legacyParsedData]);

    // Compute current group data with expected changes
    const currentGroupData = useMemo(() => {
        if (!selectedGroupData || !legacyParsedData) {
            return null;
        }

        return {
            ...selectedGroupData,
            Filter: legacyParsedData.filter,
            FilterScope: legacyParsedData.entityType,
            Managed: true
        };
    }, [selectedGroupData, legacyParsedData]);

    // ========== Render ==========

    return (
        <Box>
            {/* Success/Error Messages */}
            {legacySuccess && (
                <Alert color="green" mb="md" withCloseButton onClose={() => setLegacySuccess(null)}>
                    {legacySuccess}
                </Alert>
            )}
            {legacyError && (
                <Alert color="red" mb="md" withCloseButton onClose={() => setLegacyError(null)}>
                    {legacyError}
                </Alert>
            )}

            <Paper withBorder p="md" mb="lg">
                    <Group justify="space-between" align="center" mb="md">
                        <Title order={5}>
                            Legacy Filter Converter
                        </Title>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={handleOpenGroupSelect}
                            disabled={loadingGroups || loadingSelectedGroup}
                            leftSection={loadingGroups ? <Loader size={16} /> : <IconRefresh size={16} />}
                        >
                            Load from Group
                        </Button>
                    </Group>
                    <Text size="sm" c="dimmed" mb="md">
                        {selectedGroupDisplayName
                            ? `Converting group: ${selectedGroupDisplayName}`
                            : 'Enter a legacy filter or load from a group'}
                    </Text>

                    {/* RC Cache Status */}
                    {loadingRCCache && (
                        <Alert color="blue" mb="md">
                            <Group gap="xs" align="center">
                                <Loader size={16} />
                                Loading Responsibility Center cache for ID resolution...
                            </Group>
                        </Alert>
                    )}

                    {untranslatedRcIds.size > 0 && (
                        <Alert color="yellow" mb="md" icon={<IconAlertTriangle size={16} />}>
                            {untranslatedRcIds.size} RC ID(s) could not be resolved to GUIDs
                        </Alert>
                    )}

                    {groupSelectError && (
                        <Alert color="red" mb="md" withCloseButton onClose={() => { }}>
                            {groupSelectError}
                        </Alert>
                    )}

                    {loadingSelectedGroup && (
                        <Group justify="center" py="md">
                            <Loader />
                        </Group>
                    )}

                    {/* Manual Filter Input */}
                    <Textarea
                        label="Legacy Filter"
                        placeholder="/Person[(JobCode = '0030') or (JobCode = '0035') or (JobCode = '0036')]"
                        value={legacyFilter}
                        onChange={(e) => handleLegacyFilterChange(e.currentTarget.value)}
                        error={legacyParseError || undefined}
                        description={!legacyParseError ? 'Enter legacy filter syntax to convert to SCIM filter' : undefined}
                        mb="md"
                        disabled={loadingSelectedGroup}
                        minRows={4}
                        autosize
                        styles={{ input: { fontFamily: 'monospace', fontSize: '0.875rem' } }}
                    />

                    {/* Parsed Result */}
                    {legacyParsedData && (
                        <Alert color="green" mb="md">
                            <Text fw={600} size="sm" mb={4}>
                                Conversion Result
                            </Text>
                            <Text size="sm" mb="xs">
                                <strong>Filter Scope:</strong> {String(legacyParsedData.entityType || '')}
                            </Text>
                            <Paper withBorder p="sm" radius="sm" ff="monospace" fz="sm" style={{ wordBreak: 'break-all' }}>
                                {String(legacyParsedData.filter || '')}
                            </Paper>
                            <Group justify="space-between" align="center" mt="sm">
                                <Group gap="xs">
                                    <Button
                                        size="sm"
                                        color="green"
                                        leftSection={legacyLoading ? <Loader size={16} color="white" /> : <IconCircleCheck size={16} />}
                                        onClick={handleConvertGroupFromLegacy}
                                        disabled={!selectedGroupId || legacyLoading || loadingSelectedGroup}
                                    >
                                        {legacyLoading ? 'Applying...' : 'Apply Conversion to Group'}
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        leftSection={<IconEye size={16} />}
                                        onClick={() => setFilterPreviewOpen(true)}
                                        disabled={loadingSelectedGroup}
                                    >
                                        Preview Members
                                    </Button>
                                </Group>
                                <Tooltip label="Copy converted filter">
                                    <ActionIcon variant="subtle" size="sm" onClick={handleCopyFilter}>
                                        <IconCopy size={16} />
                                    </ActionIcon>
                                </Tooltip>
                            </Group>
                        </Alert>
                    )}

                    {/* Attribute Warnings */}
                    {attributeValidationWarning && attributeValidationWarning.length > 0 && (
                        <Alert color="yellow" mb="md" icon={<IconAlertTriangle size={16} />}>
                            <Text fw={600} size="sm" mb={4}>
                                Attribute Validation Warning
                            </Text>
                            <Text size="sm">
                                The following attributes in the filter do not exist on Person:
                            </Text>
                            <Box component="ul" mt={4} mb={0} pl="md">
                                {attributeValidationWarning.map((attr, index) => (
                                    <li key={index}>
                                        <Text size="sm" component="code">{attr}</Text>
                                    </li>
                                ))}
                            </Box>
                        </Alert>
                    )}

                    {/* View Tabs - Form / JSON Diff / Expected Payload */}
                    {selectedGroupData && legacyParsedData && (
                        <Tabs value={String(legacyTabView)} onChange={(value) => setLegacyTabView(Number(value))} mb="md">
                            <Tabs.List>
                                <Tabs.Tab value="0">Form</Tabs.Tab>
                                <Tabs.Tab value="1">JSON Diff</Tabs.Tab>
                                <Tabs.Tab value="2">Expected Payload</Tabs.Tab>
                            </Tabs.List>
                        </Tabs>
                    )}

                    {selectedGroupData && legacyParsedData && (
                        <>
                            {/* Form Tab */}
                            <Box style={{ display: legacyTabView === 0 ? 'block' : 'none' }}>
                                <Paper withBorder p="md" bg="var(--mantine-color-default-hover)">
                                    <Text fw={600} size="sm" mb="xs">
                                        Conversion Summary
                                    </Text>
                                    <Box style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                                        <Box>
                                            <Text size="xs" c="dimmed">Current State</Text>
                                            <Text size="sm"><strong>FilterScope:</strong> {typeof (selectedGroupData.FilterScope || selectedGroupData.filterScope) === 'object' ? JSON.stringify(selectedGroupData.FilterScope || selectedGroupData.filterScope) : String(selectedGroupData.FilterScope || selectedGroupData.filterScope || '—')}</Text>
                                            <Text size="xs" ff="monospace" style={{ wordBreak: 'break-all' }}>
                                                <strong>Filter:</strong> {typeof (selectedGroupData.Filter || selectedGroupData.filter) === 'object' ? JSON.stringify(selectedGroupData.Filter || selectedGroupData.filter) : String(selectedGroupData.Filter || selectedGroupData.filter || '—')}
                                            </Text>
                                            <Text size="sm"><strong>Managed:</strong> {(selectedGroupData.Managed || selectedGroupData.managed) ? 'Yes' : 'No'}</Text>
                                        </Box>
                                        <Box>
                                            <Text size="xs" c="dimmed">After Conversion</Text>
                                            <Text size="sm" c="green"><strong>FilterScope:</strong> {String(legacyParsedData.entityType || '—')}</Text>
                                            <Text size="xs" ff="monospace" c="green" style={{ wordBreak: 'break-all' }}>
                                                <strong>Filter:</strong> {String(legacyParsedData.filter || '—')}
                                            </Text>
                                            <Text size="sm" c="green"><strong>Managed:</strong> Yes</Text>
                                        </Box>
                                    </Box>
                                </Paper>
                            </Box>

                            {/* JSON Diff Tab */}
                            <Box style={{ display: legacyTabView === 1 ? 'block' : 'none' }}>
                                {selectedGroupData && currentGroupData ? (
                                    <JsonDiffViewer
                                        originalData={selectedGroupData}
                                        currentData={currentGroupData}
                                        title="Group Conversion Changes"
                                        excludedPaths={['Id', 'EntityType']}
                                        collapsible={false}
                                        defaultExpanded={true}
                                    />
                                ) : (
                                    <Box p="md">
                                        <Text size="sm" c="dimmed">
                                            No changes detected
                                        </Text>
                                    </Box>
                                )}
                            </Box>

                            {/* Expected Payload Tab */}
                            <Box style={{ display: legacyTabView === 2 ? 'block' : 'none' }}>
                                {expectedPayload ? (
                                    <JsonViewer
                                        data={expectedPayload}
                                        title="Expected PATCH Payload"
                                        description="This is the PATCH request that will be sent to convert the group to dynamic."
                                        formatBodyMultiline
                                    />
                                ) : (
                                    <Box p="md">
                                        <Text size="sm" c="dimmed">
                                            No payload available
                                        </Text>
                                    </Box>
                                )}
                            </Box>
                        </>
                    )}

                    {/* Clear Button */}
                    {(legacyFilter || selectedGroupData) && (
                        <Group justify="flex-end" mt="md">
                            <Button
                                variant="outline"
                                onClick={handleClearLegacyState}
                                disabled={legacyLoading || loadingSelectedGroup}
                                leftSection={<IconX size={16} />}
                            >
                                Clear
                            </Button>
                        </Group>
                    )}
            </Paper>

            {/* Filter Preview Dialog */}
            {legacyParsedData && (
                <FilterPreviewDialog
                    open={filterPreviewOpen}
                    onClose={() => setFilterPreviewOpen(false)}
                    filter={legacyParsedData.filter}
                    filterScope={legacyParsedData.entityType}
                />
            )}

            {/* Group Selection Dialog */}
            <Modal
                opened={groupSelectDialogOpen}
                onClose={() => {
                    setGroupSelectDialogOpen(false);
                    setGroupsPage(0);
                }}
                title={
                    <Group gap="xs" align="center">
                        <span>Select Group with Legacy Filter</span>
                        {loadingSelectedGroup && (
                            <Group gap={4} align="center">
                                <Loader size={16} />
                                <Text size="sm" c="dimmed">Resolving legacy IDs...</Text>
                            </Group>
                        )}
                    </Group>
                }
                size="lg"
            >
                <Box pos="relative">
                    {loadingSelectedGroup && (
                        <LoadingState variant="overlay" visible />
                    )}

                    {loadingGroups && groupsWithLegacyFilter.length === 0 ? (
                        <LoadingState variant="centered" minHeight={200} />
                    ) : groupsWithLegacyFilter.length === 0 ? (
                        <Text size="sm" c="dimmed" py="md">
                            No groups found with LegacyFilter property.
                        </Text>
                    ) : (
                        <Box mt="md">
                            {groupsTotalResults !== null && (
                                <Alert color="blue" mb="md">
                                    <Text size="sm">
                                        Showing <strong>{groupsWithLegacyFilter.length}</strong> of <strong>{groupsTotalResults}</strong> groups
                                    </Text>
                                </Alert>
                            )}
                            <Table.ScrollContainer minWidth={500}>
                                <Table striped highlightOnHover>
                                    <Table.Thead>
                                        <Table.Tr>
                                            <Table.Th>Group Name</Table.Th>
                                            <Table.Th ta="center">IsManaged</Table.Th>
                                            <Table.Th ta="center">FilterScope</Table.Th>
                                            <Table.Th ta="center">Filter</Table.Th>
                                            <Table.Th ta="center">Action</Table.Th>
                                        </Table.Tr>
                                    </Table.Thead>
                                    <Table.Tbody>
                                        {groupsWithLegacyFilter.map((group) => {
                                            const groupId = group.Id || group.id;
                                            const displayName = group.DisplayName || group.displayName;
                                            const isManaged = group.Managed || group.managed || group.isManaged;
                                            const hasFilterScope = !!(group.FilterScope || group.filterScope || group.hasFilterScope);
                                            const hasFilter = !!(group.Filter || group.filter || group.hasFilter);

                                            return (
                                                <Table.Tr key={groupId}>
                                                    <Table.Td><Text size="sm">{displayName}</Text></Table.Td>
                                                    <Table.Td ta="center">
                                                        {isManaged ? (
                                                            <Tooltip label="Group is managed"><IconCircleCheck size={16} color="var(--mantine-color-green-6)" /></Tooltip>
                                                        ) : (
                                                            <Tooltip label="Group is not managed"><Text size="sm" c="dimmed">—</Text></Tooltip>
                                                        )}
                                                    </Table.Td>
                                                    <Table.Td ta="center">
                                                        {hasFilterScope ? (
                                                            <Tooltip label="Group has FilterScope"><IconCircleCheck size={16} color="var(--mantine-color-green-6)" /></Tooltip>
                                                        ) : (
                                                            <Tooltip label="Group does not have FilterScope"><Text size="sm" c="dimmed">—</Text></Tooltip>
                                                        )}
                                                    </Table.Td>
                                                    <Table.Td ta="center">
                                                        {hasFilter ? (
                                                            <Tooltip label="Group has Filter"><IconCircleCheck size={16} color="var(--mantine-color-green-6)" /></Tooltip>
                                                        ) : (
                                                            <Tooltip label="Group does not have Filter"><Text size="sm" c="dimmed">—</Text></Tooltip>
                                                        )}
                                                    </Table.Td>
                                                    <Table.Td ta="center">
                                                        <Button
                                                            variant="outline"
                                                            size="xs"
                                                            onClick={() => {
                                                                handleSelectGroupForConversion(group);
                                                                setGroupSelectDialogOpen(false);
                                                            }}
                                                            disabled={loadingSelectedGroup}
                                                        >
                                                            Select
                                                        </Button>
                                                    </Table.Td>
                                                </Table.Tr>
                                            );
                                        })}
                                    </Table.Tbody>
                                </Table>
                            </Table.ScrollContainer>
                            {groupsTotalResults !== null && (
                                <Group justify="center" mt="md">
                                    <Pagination
                                        total={Math.ceil(groupsTotalResults / groupsRowsPerPage)}
                                        value={groupsPage + 1}
                                        onChange={(page) => handleGroupsPageChange(null, page - 1)}
                                    />
                                </Group>
                            )}
                        </Box>
                    )}
                </Box>
                <Group justify="flex-end" mt="md">
                    <Button variant="default" onClick={() => {
                        setGroupSelectDialogOpen(false);
                        setGroupsPage(0);
                    }}>
                        Cancel
                    </Button>
                </Group>
            </Modal>
        </Box>
    );
};
